import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TutorialSpotlight } from './TutorialSpotlight';
import { useFirstTrackTutorial } from '@/stores/tutorialStore';
import { useTutorialStore } from '@/stores/tutorialStore';
import { hapticSuccess } from '@/utils/haptics';

interface TutorialControllerProps {
  username: string | null;
}

export const TutorialController: React.FC<TutorialControllerProps> = ({ username }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    isActive,
    currentStep,
    stepIndex,
    totalSteps,
    nextStep,
    skipTutorial,
    completeTutorial,
  } = useFirstTrackTutorial(username, (key: string) => t(key as any));

  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [additionalElements, setAdditionalElements] = useState<HTMLElement[]>([]);
  const [showCelebration, setShowCelebration] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);

  // Get current page from location
  const getCurrentPage = () => {
    if (location.pathname === '/') return 'home';
    if (location.pathname === '/track') return 'track';
    if (location.pathname === '/social') return 'social';
    return '';
  };

  // Handle step changes and page navigation
  useEffect(() => {
    if (!isActive || !currentStep) return;

    const currentPage = getCurrentPage();

    // If we need to be on a different page, navigate there
    if (currentStep.page !== currentPage) {
      const pageMap: Record<string, string> = {
        home: '/',
        track: '/track',
        social: '/social',
      };
      
      if (pageMap[currentStep.page]) {
        navigate(pageMap[currentStep.page]);
      }
    }
  }, [currentStep, isActive, navigate, location.pathname]);

  // Find target element when page changes or step changes
  useEffect(() => {
    if (!isActive || !currentStep) {
      setTargetElement(null);
      setAdditionalElements([]);
      return;
    }

    // Apply delay if specified
    const delay = currentStep.delay || 0;
    
    const timer = setTimeout(() => {
      // Execute any actions for this step
      if (currentStep.action) {
        currentStep.action();
      }

      // Find target element by selector or use existing element
      if (currentStep.targetElement) {
        setTargetElement(currentStep.targetElement);
        // Also handle additional elements
        if (currentStep.additionalTargetSelectors && currentStep.additionalTargetSelectors.length > 0) {
          const additionalEls = currentStep.additionalTargetSelectors
            .map(selector => document.querySelector(selector) as HTMLElement)
            .filter(el => el !== null);
          setAdditionalElements(additionalEls);
        } else {
          setAdditionalElements([]);
        }
      } else if (currentStep.targetSelector) {
        const findElement = () => {
          console.log('[Tutorial] Looking for element with selector:', currentStep.targetSelector);
          const element = document.querySelector(currentStep.targetSelector!) as HTMLElement;
          if (element) {
            console.log('[Tutorial] ✅ Element found:', element);
            setTargetElement(element);
            
            // Always clear and re-set additional elements
            if (currentStep.additionalTargetSelectors && currentStep.additionalTargetSelectors.length > 0) {
              const additionalEls = currentStep.additionalTargetSelectors
                .map(selector => document.querySelector(selector) as HTMLElement)
                .filter(el => el !== null);
              setAdditionalElements(additionalEls);
            } else {
              // Explicitly clear when no additional selectors
              setAdditionalElements([]);
            }
            
            return true;
          }
          // If element not found, also clear additional elements
          console.log('[Tutorial] ❌ Element not found, available data-metric-key elements:', 
            Array.from(document.querySelectorAll('[data-metric-key]')).map((el: Element) => (el as HTMLElement).dataset.metricKey)
          );
          setAdditionalElements([]);
          return false;
        };

        // Try finding element immediately
        if (!findElement()) {
          // If not found, retry a few times (page might still be loading)
          let retries = 0;
          const maxRetries = currentStep.maxRetries ?? 10;
          const retryIntervalMs = currentStep.retryInterval ?? 100;
          const retryIntervalId = setInterval(() => {
            console.log(`[Tutorial] Retry ${retries + 1}/${maxRetries} finding element`);
            if (findElement() || retries >= maxRetries) {
              clearInterval(retryIntervalId);
              if (retries >= maxRetries) {
                console.error(`[Tutorial] Failed to find element after ${maxRetries} retries`);
              }
            }
            retries++;
          }, retryIntervalMs);

          return () => clearInterval(retryIntervalId);
        }
        
        // If pollPosition is configured, poll for position updates after finding element
        // This is useful for elements that animate in (like modals)
        if (currentStep.pollPosition && currentStep.pollPosition > 0) {
          const pollInterval = currentStep.pollInterval || 500;
          let pollCount = 0;
          
          console.log(`[Tutorial] Polling position ${currentStep.pollPosition} times at ${pollInterval}ms intervals`);
          
          const positionPollInterval = setInterval(() => {
            pollCount++;
            console.log(`[Tutorial] Position poll ${pollCount}/${currentStep.pollPosition}`);
            
            // Re-query the element to get updated position
            const element = document.querySelector(currentStep.targetSelector!) as HTMLElement;
            if (element) {
              // Force re-render by setting to null then back to element
              setTargetElement(null);
              setTimeout(() => {
                setTargetElement(element);
              }, 10);
            }
            
            if (pollCount >= currentStep.pollPosition!) {
              clearInterval(positionPollInterval);
              console.log('[Tutorial] Position polling complete');
            }
          }, pollInterval);
          
          return () => clearInterval(positionPollInterval);
        }
      } else {
        // No target selector or element - clear any previous spotlight
        console.log('[Tutorial] No target element for this step - clearing spotlight');
        setTargetElement(null);
        setAdditionalElements([]);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [currentStep, isActive]);

  // Force scroll to bottom for leaderboard step
  useEffect(() => {
    if (!isActive || !currentStep || currentStep.id !== 'leaderboard') {
      return;
    }

    // Scroll to bottom immediately
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });

    // Keep scrolling to bottom every 500ms to maintain position
    const scrollInterval = setInterval(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }, 500);

    return () => clearInterval(scrollInterval);
  }, [currentStep, isActive]);

  // Handle next step
  const handleNext = useCallback(() => {
    console.log('[TutorialController] handleNext called, stepIndex:', stepIndex, 'totalSteps:', totalSteps);
    if (stepIndex === totalSteps - 1) {
      // Last step - show celebration
      console.log('[TutorialController] Last step - completing tutorial');
      setShowCelebration(true);
      hapticSuccess();
      
      // Hide celebration after animation and complete tutorial
      setTimeout(() => {
        setShowCelebration(false);
        setIsFadingOut(true);
        
        // Wait for fade out animation, then complete
        setTimeout(() => {
          if (username) {
            completeTutorial();
          }
          setIsFadingOut(false);
        }, 300); // Match AnimatePresence exit duration
      }, 1500);
    } else {
      console.log('[TutorialController] Moving to next step');
      nextStep();
    }
  }, [stepIndex, totalSteps, nextStep, completeTutorial, username]);

  // Handle skip
  const handleSkip = useCallback(() => {
    setIsFadingOut(true);
    
    // Wait for fade out, then skip
    setTimeout(() => {
      skipTutorial();
      setTargetElement(null);
      setIsFadingOut(false);
    }, 300); // Match AnimatePresence exit duration
  }, [skipTutorial]);

  if (!isActive || !currentStep) return null;

  return (
    <>
      <TutorialSpotlight
        isActive={!isFadingOut}
        targetElement={targetElement}
        additionalElements={additionalElements}
        title={currentStep.title}
        description={currentStep.description}
        position={currentStep.position}
        onNext={handleNext}
        onSkip={handleSkip}
        nextButtonText={stepIndex === totalSteps - 1 ? t('onboarding.integrated_tutorial.finish' as any) : t('onboarding.integrated_tutorial.next' as any)}
        showSkip={true}
        customContent={currentStep.customContent}
        tooltipDelay={currentStep.tooltipDelay}
      />
      
      {showCelebration && (
        <EmojiCelebration emojis={getRandomEmojis()} />
      )}
    </>
  );
};

// Emoji celebration component
interface EmojiCelebrationProps {
  emojis: string[];
}

const EmojiCelebration: React.FC<EmojiCelebrationProps> = ({ emojis }) => {
  const [particles, setParticles] = useState<Array<{
    id: number;
    emoji: string;
    x: number;
    delay: number;
  }>>([]);

  useEffect(() => {
    const particleCount = 30;
    const newParticles = Array.from({ length: particleCount }, (_, i) => ({
      id: i,
      emoji: emojis[Math.floor(Math.random() * emojis.length)],
      x: Math.random() * 100,
      delay: Math.random() * 0.5,
    }));
    setParticles(newParticles);
  }, [emojis]);

  return (
    <div className="fixed inset-0 pointer-events-none z-[10000] overflow-hidden">
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute text-4xl animate-emoji-fall"
          style={{
            left: `${particle.x}%`,
            top: '-10%',
            animationDelay: `${particle.delay}s`,
            animationDuration: `${2 + Math.random()}s`,
          }}
        >
          {particle.emoji}
        </div>
      ))}
      
      <style>{`
        @keyframes emoji-fall {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(110vh) rotate(${Math.random() * 720 - 360}deg);
            opacity: 0;
          }
        }
        .animate-emoji-fall {
          animation: emoji-fall linear forwards;
        }
      `}</style>
    </div>
  );
};

// Helper function to get random user emojis
const getRandomEmojis = () => {
  const emojiSets = [
    ['🎉', '🎊', '✨', '🌟', '⭐'],
    ['💪', '🔥', '🚀', '🎯', '💯'],
    ['🏆', '👏', '🙌', '🎈', '🎁'],
  ];
  
  const randomSet = emojiSets[Math.floor(Math.random() * emojiSets.length)];
  return randomSet;
};

// Hook to register target elements
export const useTutorialTarget = (stepId: string, ref: React.RefObject<HTMLElement>) => {
  const setTargetElement = useTutorialStore((state) => state.setTargetElement);
  
  useEffect(() => {
    if (ref.current) {
      setTargetElement(stepId, ref.current);
    }
    
    return () => {
      setTargetElement(stepId, null);
    };
  }, [stepId, ref, setTargetElement]);
};
